/** Pagination nav with ellipsis logic. Shows first, last, current ± 1, with gaps collapsed to ellipsis. */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';
import { Button } from './Button';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

function getPageNumbers(page: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];

  if (page > 3) pages.push('ellipsis');

  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (page < totalPages - 2) pages.push('ellipsis');

  if (totalPages > 1) pages.push(totalPages);

  return pages;
}

export function Pagination({ page, totalPages, onPageChange, className }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(page, totalPages);

  return (
    <nav
      className={cn('flex items-center justify-center gap-1', className)}
      aria-label="Pagination"
    >
      <Button
        variant="ghost"
        size="icon"
        isDisabled={page <= 1}
        onPress={() => onPageChange(page - 1)}
        aria-label="Previous page"
        className="h-8 w-8"
      >
        <ChevronLeft size={16} strokeWidth={2} style={{ transform: 'translateZ(0)' }} />
      </Button>

      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span
            key={`ellipsis-${i}`}
            className="flex h-8 w-8 items-center justify-center text-xs"
            style={{ color: 'var(--f-fg-muted)' }}
          >
            ...
          </span>
        ) : (
          <Button
            key={p}
            variant={p === page ? 'outline' : 'ghost'}
            size="sm"
            onPress={() => onPageChange(p)}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
            className={cn(
              'h-8 w-8 rounded-lg p-0 tabular-nums text-xs',
              p === page && 'border-[var(--f-accent)] text-[var(--f-accent-text)]'
            )}
          >
            {p}
          </Button>
        )
      )}

      <Button
        variant="ghost"
        size="icon"
        isDisabled={page >= totalPages}
        onPress={() => onPageChange(page + 1)}
        aria-label="Next page"
        className="h-8 w-8"
      >
        <ChevronRight size={16} strokeWidth={2} style={{ transform: 'translateZ(0)' }} />
      </Button>
    </nav>
  );
}
